import { html } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_DATA_PROTECTION_RULES_FORM } from '../../../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../../../../../../ui/gzForm/formGroup/form-group';
import { InputWidthValues } from '../../../../../../../../../../ui/gzInput';
import { GzCheckboxWebComponent } from '../../../../../../../../../../ui/gzCheckbox';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { GzDropdownWebComponent } from '../../../../../../../../../../ui/gzDropdown';
import { getDataProtectionRuleTypeDropdownOptions } from '../../../../../../../utils/uiSettings/contentControl/content-control.values';
import {
  ContentControlDataProtectionRule,
  ContentControlDataProtectionRuleType,
} from '@nirvana/shared/dtos/types/policies/common';

@customElement(WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_DATA_PROTECTION_RULES_FORM)
export class GzContentControlWebDataProtectionRulesFormWebComponent extends GzPolicyFullPageWindowFormWebComponent<ContentControlDataProtectionRule> {
  @query('#name-field')
  private nameField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#type-field')
  private typeField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#string-field')
  private stringField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#scan-web-traffic-field')
  private scanWebTrafficField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#scan-email-traffic-field')
  private scanEmailTrafficField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#match-whole-words-field')
  private matchWholeWordsField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#match-case-field')
  private matchCaseField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  /**
   * Some fields from the current component are connected. When an external error is set,
   * any change on another field should remove external validation from server.
   */
  private shouldBeRevalidatedOnFieldChange = false;

  public constructor(value?: ContentControlDataProtectionRule) {
    super(value);
  }

  static get styles() {
    return styles;
  }

  public setItemNameError(error: string) {
    this.nameField.setError(error);
  }

  public setItemStringError(error: string) {
    this.shouldBeRevalidatedOnFieldChange = true;
    this.typeField.setError(error);
    this.stringField.setError(error);
  }

  protected getConvertedFormValue() {
    return this.formGroup.value;
  }

  createFormGroup(): void {
    this.nameField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.typeField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.stringField.Validators = [RequiredValidator(translateFn('Required field'))];

    this.formGroup = FormGroup.build({
      name: this.nameField,
      type: this.typeField,
      string: this.stringField,
      scanWebTraffic: this.scanWebTrafficField,
      scanEmailTrafiic: this.scanEmailTrafficField,
      matchWholeWords: this.matchWholeWordsField,
      matchCase: this.matchCaseField,
    });
  }

  setFormGroupValue() {
    if (!this.value) {
      return;
    }

    this.formGroup.value = {
      name: this.value.name,
      type: this.value.type,
      string: this.value.string,
      scanWebTraffic: this.value.scanWebTraffic,
      scanEmailTrafiic: this.value.scanEmailTrafiic,
      matchWholeWords: this.value.matchWholeWords,
      matchCase: this.value.matchCase,
    };
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div class="rules-form-container">
        <gz-title fontWeight="600" fontSize="11" class="module-subtitle">${translateFn('Details')}</gz-title>
        <gz-form-field id="name-field" class="label-mw-250 mt-2" label="${translateFn('Rule name')}" required>
          <gz-input
            inputWidth="${InputWidthValues.LARGE}"
            gz-form-input
            placeholder="${translateFn('Rule name')}"
          ></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-form-field id="type-field" label="${translateFn('Rule type')}" required>
          <gz-dropdown
            .value="${ContentControlDataProtectionRuleType.ADDRESS}"
            gz-form-input
            options="${JSON.stringify(getDataProtectionRuleTypeDropdownOptions())}"
            @selectionChanged="${e => {
              if (this.shouldBeRevalidatedOnFieldChange) {
                this.shouldBeRevalidatedOnFieldChange = false;
                this.stringField.validate(true);
              }
            }}"
          ></gz-dropdown>
        </gz-form-field>

        <gz-form-field id="string-field" class="label-mw-250 mt-2" label="${translateFn('Data')}" required>
          <gz-input
            inputWidth="${InputWidthValues.LARGE}"
            gz-form-input
            placeholder="${translateFn('Enter the data to protect')}"
            @change="${e => {
              if (this.shouldBeRevalidatedOnFieldChange) {
                this.shouldBeRevalidatedOnFieldChange = false;
                this.typeField.validate(true);
              }
            }}"
          ></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-text fontSize="12" fontWeight="400"
          >${translateFn(
            'Enter the data you want to protect. For example: phone number, name, or email. Any combination of alphanumeric and special characters such as {specialCharacter1}, {specialCharacter2}, or {specialCharacter3} is accepted. Enter at least 5 characters to prevent accidental blocking of emails or webpages.',
            {
              specialCharacter1: '@',
              specialCharacter2: '#',
              specialCharacter3: '$',
            }
          )}
        </gz-text>

        <gz-title fontWeight="600" fontSize="11" class="module-subtitle mt-6">${translateFn('Settings')}</gz-title>
        <gz-form-field id="scan-web-traffic-field" class="mt-2">
          <gz-checkbox gz-form-input label="${translateFn('Scan web (HTTP) traffic')}" checked></gz-checkbox>
        </gz-form-field>
        <gz-form-field id="scan-email-traffic-field">
          <gz-checkbox gz-form-input label="${translateFn('Scan email (SMTP) traffic')}" checked></gz-checkbox>
        </gz-form-field>
        <gz-form-field id="match-whole-words-field">
          <gz-checkbox gz-form-input label="${translateFn('Match whole words')}"></gz-checkbox>
        </gz-form-field>
        <gz-form-field id="match-case-field">
          <gz-checkbox gz-form-input label="${translateFn('Match case')}"></gz-checkbox>
        </gz-form-field>
      </div>
    `;
  }
}
